import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Send, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function AiAssistPanel({ entityType, entityId, onLogCommunication }) {
  const [summary, setSummary] = useState("");
  const [draft, setDraft] = useState("");
  const [channel, setChannel] = useState("email");
  const [loadingSum, setLoadingSum] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [approved, setApproved] = useState(false);

  useEffect(() => {
    setSummary(""); setDraft(""); setApproved(false);
  }, [entityId]);

  const runSummarize = async () => {
    setLoadingSum(true);
    try {
      const r = await api.post("/ai/summarize", { entity_type: entityType, entity_id: entityId });
      setSummary(r.data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI unavailable");
    } finally {
      setLoadingSum(false);
    }
  };

  const runDraft = async () => {
    setLoadingDraft(true);
    setApproved(false);
    try {
      const r = await api.post("/ai/draft", {
        entity_type: entityType,
        entity_id: entityId,
        channel,
      });
      setDraft(r.data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI unavailable");
    } finally {
      setLoadingDraft(false);
    }
  };

  const approveAndLog = async () => {
    if (!draft.trim()) return;
    try {
      await api.post("/communications", {
        type: channel,
        direction: "outbound",
        subject: channel === "email" ? draft.split("\n")[0].replace(/^Subject:\s*/i, "").slice(0, 120) : null,
        body: draft,
        related_type: entityType,
        related_id: entityId,
      });
      setApproved(true);
      toast.success("Communication logged to timeline");
      onLogCommunication?.();
    } catch (e) {
      toast.error("Failed to log");
    }
  };

  return (
    <div
      data-testid="ai-assist-panel"
      className="rounded-xl p-4 relative"
      style={{
        background: "#fff",
        border: "1px solid transparent",
        backgroundImage: "linear-gradient(#fff,#fff),linear-gradient(120deg,#0EA5FF,#5FBF3A)",
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-[var(--miro-blue)]"/>
        <div className="font-display font-bold text-[var(--miro-navy)]">AI Assist</div>
        <span className="text-[10px] uppercase tracking-widest text-[var(--miro-text-muted)]">Claude Sonnet 4.5</span>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-[var(--miro-green-600)]">Human-approve</span>
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="miro-overline">Lead summary</div>
            <Button
              data-testid="ai-summarize-btn"
              size="sm"
              variant="outline"
              onClick={runSummarize}
              disabled={loadingSum}
              className="h-7 text-xs"
            >
              {loadingSum ? <Loader2 size={13} className="animate-spin mr-1"/> : <Sparkles size={13} className="mr-1"/>}
              Summarize
            </Button>
          </div>
          <Textarea
            data-testid="ai-summary-textarea"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Click Summarize to generate a bullet-point overview…"
            rows={4}
            className="text-sm"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1 gap-2">
            <div className="miro-overline">Follow-up draft</div>
            <div className="flex items-center gap-2">
              <select
                data-testid="ai-channel-select"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="text-xs border border-[var(--miro-border)] rounded px-2 h-7 bg-white"
              >
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
              <Button
                data-testid="ai-draft-btn"
                size="sm"
                variant="outline"
                onClick={runDraft}
                disabled={loadingDraft}
                className="h-7 text-xs"
              >
                {loadingDraft ? <Loader2 size={13} className="animate-spin mr-1"/> : <Sparkles size={13} className="mr-1"/>}
                Draft
              </Button>
            </div>
          </div>
          <Textarea
            data-testid="ai-draft-textarea"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setApproved(false); }}
            placeholder="Click Draft to generate a follow-up message. You must review before it is logged."
            rows={7}
            className="text-sm"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button
              data-testid="ai-approve-btn"
              size="sm"
              onClick={approveAndLog}
              disabled={!draft.trim() || approved}
              className="h-8 text-xs"
              style={{ background: approved ? "var(--miro-green)" : "var(--miro-navy)" }}
            >
              {approved ? <><Check size={13} className="mr-1"/>Logged to timeline</> : <><Send size={13} className="mr-1"/>Approve & log</>}
            </Button>
            <span className="text-[11px] text-[var(--miro-text-muted)]">
              Nothing is sent automatically. You control the send.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
